import Service, { service } from '@ember/service';
import { getSingleValueForTasField } from '../utils/tuition-assistance/fields.ts';
import { limitsEvaluationDate } from '../utils/tuition-assistance/limits-evaluation-date.ts';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { date, minValue, maxValue, object, pipe } from 'valibot';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { EligibilityStatus } from 'tio-data/data/resources/eligibility-status.type';
import type FeaturesService from './features.ts';
import type IntlService from 'ember-intl/services/intl';

// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from './session-context.ts';
import type { TasApplicationExtension as TASApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasEligibilityExtension as TasEligibilityModel } from 'tio-data/data/resources/tas-eligibility.ext';
import type { TasParticipantExtension as TasParticipantModel } from 'tio-data/data/resources/tas-participant.ext';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type TuitionAssistanceLimitsSummaryComponent from '../components/tuition-assistance/limits-summary.gts';
import { query } from '@warp-drive/utilities/json-api';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import { CONTRACT_HEADER } from 'tio-data/handlers/type-normalization-handler';
import {
  buildTasTransitionFieldData,
  requestEvidenceApprovalRequest,
} from 'tio-data/requests/tas-application-actions';
import { buildTasActorFromSession } from 'tio-data/utils/tas-actor';
import { reloadRecord } from 'tio-data/requests/save-record';
import { eligibilityStatusFindRecord } from 'tio-data/requests/eligibility-status';

const EVIDENCE_REVIEW_UNDERWAY_STATES = new Set([
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

type LimitsRequestParamsSignature = {
  employee_ids: string[];
  excluded_item_ids: string[];
  company_id: string;
  product: string;
  scheme?: string;
  batch?: string;
  group?: string | null;
  date?: string;
  date_based_on?: string;
  course_start?: string;
  course_end?: string;
  end_of_year_cutoff?: number | null;
  requester: string | null;
  approval?: string | null;
  payment_date?: string;
  dependent_id?: string;
  application_id?: string;
  is_prepaid?: boolean;
};

type LimitsStatusRequestResponseSignature = {
  data: LimitsStatusDataSignature[];
};

type LimitName =
  | 'company_annual'
  | 'company'
  | 'product_annual'
  | 'product'
  | 'batch_annual'
  | 'batch'
  | 'scheme_annual'
  | 'scheme'
  | 'none';

export type LimitCapacity = 'TAS.LimitCapacity.MONETARY' | 'TAS.LimitCapacity.CREDITS';
export type LimitProduct = 'TA' | 'SLR' | 'DEPENDENT_TA';

export type LimitsStatusDataSignature = {
  annual_end_date: string;
  annual_start_date: string;
  as_of_date?: string;
  available: number | null;
  available_with_override: number | null;
  details: {
    company: LimitsDetailSignature;
    product: LimitsDetailSignature;
    batch: LimitsDetailSignature;
    scheme: LimitsDetailSignature;
  };
  employee_id: string;
  hard_limit: LimitName;
  limit_capacity?: LimitCapacity;
  product: string | null;
  scheme: string | null;
  soft_limit: LimitName;
  external_annual_usage?: number;
};

export type LimitsDetailSignature = {
  annual_limit: number | null;
  annual_usage: number;
  available: number | null;
  balance_multiplier?: number;
  hard: boolean | null;
  limit: number | null;
  one_month_limit: number | null;
  one_month_usage: number;
  six_month_limit: number | null;
  six_month_usage: number;
  taxable_limit: number | null;
  three_month_limit: number | null;
  three_month_usage: number;
  usage: number;
  external_annual_usage?: number;
};

type JsonApiLimitStatusResource = {
  id?: string | number;
  type?: string;
  attributes?: Record<string, unknown>;
};

type JsonApiLimitStatusDocument = {
  data?: JsonApiLimitStatusResource[];
};

function underscoreKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(underscoreKeysDeep);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, inner]) => [
        key.replace(/-/g, '_'),
        underscoreKeysDeep(inner),
      ])
    );
  }
  return value;
}

function moneyNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

/**
 * Maps the contract-2 document of GET /api/v2/limits/status — JSON:API with
 * dasherized attribute keys and money as two-decimal strings — back onto
 * LimitsStatusDataSignature, so the shape every consumer reads is unchanged by
 * the contract flip. Money strings become numbers again (the app does cents
 * arithmetic on them), hard/soft limit stay the limit NAMES the approval
 * banners branch on, and the resource id supplies employee_id.
 */
export function parseLimitStatuses(
  document: JsonApiLimitStatusDocument
): LimitsStatusDataSignature[] {
  return (document.data ?? []).map((resource) => {
    const attrs = underscoreKeysDeep(resource.attributes ?? {}) as LimitsStatusDataSignature;
    return {
      ...attrs,
      employee_id: String(resource.id ?? attrs.employee_id ?? ''),
      available: moneyNumber(attrs.available),
      available_with_override: moneyNumber(attrs.available_with_override),
    };
  });
}

export type ApplicationValidationResponseSignature = {
  can_submit: boolean;
  /** Cents (or credits for credit-capped limits) the request must shrink by; 0 when submittable. */
  reduction_needed: number;
  /** True when no limits status could be materialized, so no over-limit math ran. */
  missing_status?: boolean;
};

type CourseDateValidationResult = {
  date: Date;
  message: string;
};

export default class TuitionAssistanceService extends Service {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;

  activeLimitsSummaryComponents: Set<TuitionAssistanceLimitsSummaryComponent> = new Set();

  @tracked activeEligibilities: TasEligibilityModel[] = [];
  @tracked limitsData: LimitsStatusDataSignature | undefined = undefined;
  @tracked gpaBasedAmount: number = 0;
  @tracked programTemplate: TASProgramTemplateModel | undefined = undefined;

  // Uses the host configured at app boot by setBuildURLConfig.
  buildUrl(resourcePath: string, subPath?: string): string {
    const opts: QueryUrlOptions = {
      identifier: { type: resourcePath },
      op: 'query',
      resourcePath,
    };
    const base = buildBaseURL(opts);
    return subPath ? `${base}/${subPath.replace(/^\/+/, '')}` : base;
  }

  // Reads authContext at call time without an Ember owner.
  get tioAuthHeaders(): Headers {
    return buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
      contentType: 'application/json',
      accept: 'application/json',
    });
  }

  get availableVoucherAmount() {
    return this.limitsData?.available ?? 0;
  }

  getApplicationLimitStatusByEmployeeId = async (
    employee: EmployeeModel
  ): Promise<LimitsStatusDataSignature | undefined> => {
    const baseUrl = this.buildUrl('limits/status');

    const company = employee.company;
    const employeeId = employee.id;
    const companyId = company?.id;

    if (!employeeId || !companyId) {
      console.error(
        'Error in `getApplicationLimitStatusByEmployeeId`: Employee or company id is missing.',
        {
          employeeId,
          companyId,
        }
      );
      return undefined;
    }

    const params: LimitsRequestParamsSignature = {
      employee_ids: [employeeId],
      excluded_item_ids: [],
      company_id: companyId,
      requester: 'employee',
      product: 'TA',
    };

    // Serialize GET params the same way adapter.ajax({ data }) did.
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (Array.isArray(v)) {
        for (const item of v) {
          qs.append(`${k}[]`, String(item));
        }
      } else if (v !== undefined && v !== null) {
        qs.set(k, String(v));
      }
    }

    try {
      const response = await fetch(`${baseUrl}?${qs.toString()}`, {
        headers: this.tioAuthHeaders,
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch limits status: ${response.status}`);
      }

      const limitsStatus: LimitsStatusRequestResponseSignature = await response.json();
      const limitsData: LimitsStatusDataSignature | undefined = limitsStatus?.data?.[0];
      return limitsData;
    } catch (error) {
      console.error('Error fetching limits status:', error);
      throw error;
    }
  };

  /**
   * Fetch V2 limits under X-Tio-Contract: 2 and parse them into LimitsStatusDataSignature.
   * @param application Optional context for application-specific limits.
   * @param date Local YYYY-MM-DD fixing the benefit window. Omitted, the API uses the program limit
   * basis; application callers pass limitsEvaluationDate(application) to preserve the CourseCa$h
   * evaluate-today rule.
   * @param product Which limit tree to read. Omitted with an application, the API picks it from
   * that application (the dependent's limit for a dependent program), the same way its own limit
   * checks do; omitted otherwise, TA.
   * @returns The parsed limits status.
   */
  getV2LimitsStatus = async (
    employee: EmployeeModel,
    application?: TASApplicationModel,
    date?: string,
    programInstanceId?: string,
    product?: LimitProduct,
    planId?: string
  ): Promise<LimitsStatusDataSignature | undefined> => {
    const url = this.buildUrl('api/v2/limits/status');

    if (!employee.id) {
      console.error('Error fetching simplified limits status: employee id is missing.', {
        employeeId: employee.id,
        applicationId: application?.id,
      });
      return undefined;
    }

    const params = new URLSearchParams();
    params.append('employee_ids[]', employee.id);
    const resolvedProduct = product ?? (application?.id ? undefined : 'TA');
    if (resolvedProduct) {
      params.append('product', resolvedProduct);
    }

    if (application?.id) {
      params.append('application_id', application.id);
    }

    if (programInstanceId) {
      params.append('program_instance_id', programInstanceId);
    }

    if (planId) {
      params.append('plan_id', planId);
    }

    params.append('paid_only', 'false');

    if (date) {
      params.append('date', date);
    }

    try {
      const limitsData = await this.#fetchLimitsStatus(url, params);
      if (limitsData) {
        this.limitsData = limitsData;
      }

      return limitsData;
    } catch (error) {
      console.error('Error fetching simplified limits status:', {
        employeeId: employee.id,
        applicationId: application?.id,
        error,
      });
      throw error;
    }
  };

  /**
   * One application's limits status from GET /api/v2/limits/status, without writing the
   * service's shared `limitsData`, so a caller can read many applications side by side. The API
   * picks the application's limit tree (the dependent's for a dependent program). Dated like the
   * approval page: `limitsEvaluationDate` rules are the caller's to apply.
   *
   * @param date - As-of date, local `YYYY-MM-DD`; omitted, the API dates it from the program's
   *   limit basis
   * @returns The employee's row, or undefined when the document carries none
   * @throws when the response is not ok (403 for a caller outside the admin gate)
   */
  fetchApplicationLimitsStatus = async (
    employeeId: string,
    applicationId: string,
    date?: string
  ): Promise<LimitsStatusDataSignature | undefined> => {
    const params = new URLSearchParams();
    params.append('employee_ids[]', employeeId);
    params.append('application_id', applicationId);
    params.append('paid_only', 'false');
    if (date) {
      params.append('date', date);
    }
    return this.#fetchLimitsStatus(this.buildUrl('api/v2/limits/status'), params);
  };

  async #fetchLimitsStatus(
    url: string,
    params: URLSearchParams
  ): Promise<LimitsStatusDataSignature | undefined> {
    const headers = this.tioAuthHeaders;
    headers.set(CONTRACT_HEADER, '2');

    const response = await fetch(`${url}?${params.toString()}`, {
      headers,
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch limits status: ${response.status}`);
    }

    const document: JsonApiLimitStatusDocument = await response.json();
    return parseLimitStatuses(document)[0];
  }

  getV2SlrLimitsStatus = async (
    employee: EmployeeModel,
    planId?: string
  ): Promise<LimitsStatusDataSignature | undefined> => {
    return this.getV2LimitsStatus(employee, undefined, undefined, undefined, 'SLR', planId);
  };

  /**
   * Fetches a fresh, materialized eligibility decision for one TAS application.
   *
   * @param applicationId - The application whose lock-date decision is required
   * @returns `undefined` only when a successful document contains no resource
   */
  getEligibilityStatus = async (applicationId: string): Promise<EligibilityStatus | undefined> => {
    try {
      const { content } = (await this.store.request(
        eligibilityStatusFindRecord(applicationId)
      )) as { content: { data?: EligibilityStatus } };
      return content.data;
    } catch (error) {
      console.error('Error fetching eligibility status:', {
        applicationId,
        error,
      });
      throw error;
    }
  };

  /**
   * Submit-time limits backstop: fetches a fresh limits status and checks the
   * requested amount against the remaining benefit. Policy overrides and
   * templates configured to allow beyond-limit submission return immediately;
   * every other invocation makes an uncached network call.
   *
   * A status that cannot be materialized at all is distinct from a real status
   * with `available: null` (unlimited). By default a missing status fails open
   * (`can_submit: true`, flagged via `missing_status`) — legacy behavior the
   * approver-side submit-for-user flow relies on. Pass
   * `onMissingStatus: 'block'` to fail closed instead, matching the guided
   * wizard's step gate, which treats an unverifiable status as blocking.
   *
   * The status is dated by `limitsEvaluationDate`: a CourseCa$h application awaiting
   * courses approval is checked against today's benefit window, since approval funds
   * the card at once; everything else is dated by the API from the program's basis.
   *
   * @throws when the limits-status request itself fails for a non-exempt application
   */
  validateAmountRequestedAgainstCurrentLimitsStatus = async (
    application: TASApplicationModel,
    employee: EmployeeModel,
    options: { onMissingStatus?: 'allow' | 'block' } = {}
  ): Promise<ApplicationValidationResponseSignature> => {
    const allowSubmissionBeyondLimits =
      application.ignorePolicies ||
      application.tasProgramInstance.tasProgramTemplate.allowPreApprovalSubmissionBeyondLimit;
    if (allowSubmissionBeyondLimits) {
      return {
        can_submit: true,
        reduction_needed: 0,
      };
    }

    const limitStatus = await this.getV2LimitsStatus(
      employee,
      application,
      limitsEvaluationDate(application)
    );

    if (!limitStatus) {
      return {
        can_submit: options.onMissingStatus !== 'block',
        reduction_needed: 0,
        missing_status: true,
      };
    }

    // `available: null` on a real status means unlimited — not unknown.
    const remainingBenefit = limitStatus.available ?? null;
    if (remainingBenefit === null) {
      return {
        can_submit: true,
        reduction_needed: 0,
      };
    }

    const isCreditBased = limitStatus?.limit_capacity === 'TAS.LimitCapacity.CREDITS';
    const amountRequested = isCreditBased
      ? application.totalCredits || 0
      : application.requestedTotal || 0;
    const remainder = remainingBenefit - amountRequested;
    const isValid = remainder >= 0;

    return {
      can_submit: isValid,
      reduction_needed: isValid ? 0 : Math.abs(remainder),
    };
  };

  triggerAutoEvidenceApprovalForApplication = async (
    application: TASApplicationModel,
    comment: string = ''
  ) => {
    if (!getSingleValueForTasField('WAIVE_EVIDENCE_APPROVAL_REQUIREMENT', application.fields)) {
      console.error(
        `Application ${application.id} cannot be submitted for auto approval without evidence approval waiver.`
      );
      return;
    }

    await this.autoApproveEvidenceTask.perform(application, comment);
  };

  /* At this point, the application should already have evidence approval waived by courses approval.
   * That means that as soon as we auto-request evidence approval, it should move to fulfilled as we expect.
   * This action lives on a service because of the distributed nature of the state machine. We need to ensure
   * this task is not cancelled because of a parent component being destroyed.
   * However, we do draw the line at 15 seconds of polling for the application
   * to move into valid state for requesting evidence approval.
   */
  autoApproveEvidenceTask = task(
    { maxConcurrency: 10, enqueue: true },
    async (application: TASApplicationModel, comment: string = '') => {
      let attemptCount = 0;

      while (
        application.state !== 'TAS.ApplicationState.ATTEND' &&
        !EVIDENCE_REVIEW_UNDERWAY_STATES.has(application.state) &&
        attemptCount < 30
      ) {
        attemptCount++;
        await timeout(500);
        await reloadRecord(this.store, application);
      }

      // tio-tas requests evidence approval for waived applications itself and may already be past ATTEND.
      if (application.state !== 'TAS.ApplicationState.ATTEND') return;

      try {
        await this.store.request(
          requestEvidenceApprovalRequest(
            application.id as string,
            buildTasActorFromSession(this.sessionContext),
            comment,
            buildTasTransitionFieldData(application.fields)
          )
        );
        await reloadRecord(this.store, application);
      } catch (e) {
        console.error('autoApproveEvidenceTask encountered an error', {
          applicationId: application.id,
          error: e,
        });
      }
    }
  );

  registerLimitsSummaryComponent = (component: TuitionAssistanceLimitsSummaryComponent) => {
    this.activeLimitsSummaryComponents.add(component);
  };

  unregisterLimitsSummaryComponent = (component: TuitionAssistanceLimitsSummaryComponent) => {
    this.activeLimitsSummaryComponents.delete(component);
  };

  refreshActiveLimitsSummaryComponents = () => {
    try {
      this.activeLimitsSummaryComponents.forEach((component) => component.refreshData());
    } catch (e) {
      console.error('Error refreshing active limits summary components', {
        error: e,
      });
    }
  };

  loadEligibilitiesForCurrentEmployee = async (participant?: TasParticipantModel) => {
    const currentEmployee = this.sessionContext.currentEmployee;
    if (!currentEmployee) {
      console.warn(
        '[tuition-assistance] loadEligibilitiesForCurrentEmployee called without a currentEmployee'
      );
      this.activeEligibilities = [];
      return;
    }
    try {
      let participantToUse = participant;

      // If no participant provided, query for it (fallback for backward compatibility)
      if (!participantToUse) {
        const { content } = (await this.store.request(
          query<TasParticipantModel>('tas-participant', {
            'filter[employee]': currentEmployee.id,
            include: 'tas-eligibilities',
          })
        )) as { content: { data: TasParticipantModel[] } };
        const participants = content.data;

        if (!participants || participants.length === 0) {
          console.error('loadEligibilitiesForCurrentEmployee: No participants found', {
            employeeId: currentEmployee.id,
          });
          this.activeEligibilities = [];
          return;
        }

        participantToUse = participants[0];
      }

      const eligibilities = participantToUse?.tasEligibilities || [];

      if (eligibilities.length === 0) {
        console.warn(
          'loadEligibilitiesForCurrentEmployee: No eligibilities found for participant',
          {
            employeeId: currentEmployee.id,
            participantId: participantToUse?.id,
          }
        );
      }

      this.activeEligibilities = eligibilities.filter(
        (model: TasEligibilityModel) => model.isActive
      );
    } catch (error) {
      console.error('loadEligibilitiesForCurrentEmployee: Error loading eligibilities', {
        employeeId: currentEmployee.id,
        error,
      });
      this.activeEligibilities = [];
    }
  };

  hasActiveEligibilityForProgramTemplate = (programTemplate: TASProgramTemplateModel) => {
    // Defensive check: if activeEligibilities is empty, it might not have been loaded yet
    if (this.activeEligibilities.length === 0) {
      console.warn('hasActiveEligibilityForProgramTemplate: activeEligibilities is empty', {
        programTemplateId: programTemplate.id,
        programTemplateCode: programTemplate.code,
      });
    }

    return this.activeEligibilities.some(
      (eligibility: TasEligibilityModel) => eligibility.code === programTemplate.code
    );
  };

  waitingPeriodEndDate = (waitingPeriod: number, tasParticipant: TasParticipantModel) => {
    const waitingPeriodToNumber = Number(waitingPeriod);
    if (!tasParticipant?.employmentStartDate || waitingPeriodToNumber === 0) {
      return ''; // Return '' to indicate that the waiting period end date can't be calculated or is 0
    }

    const hireDate = new Date(tasParticipant.employmentStartDate);
    const endDate = new Date(hireDate);
    endDate.setDate(hireDate.getDate() + waitingPeriodToNumber);
    return endDate;
  };

  ineligibleBasedOnWaitingPeriod = (
    waitingPeriod: number,
    tasParticipant: TasParticipantModel,
    dateToCheck?: string
  ) => {
    const endDate = this.waitingPeriodEndDate(waitingPeriod, tasParticipant);
    if (!endDate) {
      return false; // If no end date, participant is not ineligible based on waiting period
    }

    // If no dateToCheck (typically from course start date) is provided, use today's date
    const applicationDate = dateToCheck ? new Date(dateToCheck) : new Date();
    const result = applicationDate < endDate;

    return result;
  };

  setTotalBasedOnGPA = (gpa: number) => {
    // values are in cents
    if (gpa >= 3.5) {
      this.gpaBasedAmount = 300000;
    } else if (gpa >= 2.75) {
      this.gpaBasedAmount = 225000;
    } else if (gpa >= 2.5) {
      this.gpaBasedAmount = 75000;
    } else {
      this.gpaBasedAmount = 0;
    }
    return this.gpaBasedAmount;
  };

  addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }

  firstDayToSubmitApplicationForPreApproval(
    programTemplate: TASProgramTemplateModel
  ): CourseDateValidationResult | null {
    const leadTimeDays = Number(programTemplate.coursesPreApprovalSubmissionMaxLeadTime) || null;
    if (!leadTimeDays) {
      return null;
    }

    const today = new Date();
    const submissionDeadline = this.addDays(today, leadTimeDays);
    const formattedDeadline = this.intl.formatDate(submissionDeadline, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return {
      date: submissionDeadline,
      message: `Your employer requires that you submit your application within ${leadTimeDays} days of the start date. Change date to be on or before ${formattedDeadline}.`,
    };
  }

  courseMaximumBeginDateWithErrorMessaging(
    programTemplate: TASProgramTemplateModel
  ): CourseDateValidationResult | null {
    // If the program has a defined pre-approval submission lead time, no error messaging is needed.
    if (!programTemplate.coursesPreApprovalSubmissionMaxLeadTime) {
      return null;
    }
    const preApprovalMaxLeadTimeResult =
      this.firstDayToSubmitApplicationForPreApproval(programTemplate);
    if (!preApprovalMaxLeadTimeResult) {
      return {
        date: new Date('2099-01-01'),
        message: this.intl.t(
          'tuition_assistance.program_details.courses.validation.defaults.begin_date'
        ),
      };
    }

    return preApprovalMaxLeadTimeResult;
  }

  /**
   * Earliest course begin date the pre-approval lead-time policy still accepts, with the
   * message naming the policy, or null when the template sets no lead-time bound.
   *
   * Both bounds it reads are measured from a submission date, so the window slides forward
   * with every day an application spends awaiting a decision: measured from today, an
   * application returned for changes fails on resubmit a deadline its first submission met.
   * Measuring from `submittedAt` instead reproduces the original verdict exactly — the start
   * date that cleared the bound still clears it, and one moved earlier still does not.
   *
   * @param programTemplate template carrying the lead-time policy fields
   * @param submittedAt when the application was first submitted, as an ISO timestamp;
   *   omit for a never-submitted draft, which is then measured from today
   * @return the bound and its policy message, or null when none applies
   */
  lastPossibleBeginDateWithErrorMessaging(
    programTemplate: TASProgramTemplateModel,
    submittedAt?: string
  ): CourseDateValidationResult | null {
    let daysOffset: number | null = null;
    let policyMessage = '';

    const { lastDayToSubmitApplicationForPreApproval, coursesPreApprovalSubmissionMinLeadTime } =
      programTemplate;

    if (lastDayToSubmitApplicationForPreApproval) {
      daysOffset = -Number(lastDayToSubmitApplicationForPreApproval) || null;
      if (daysOffset) {
        policyMessage = `Your employer requires that you submit your application within ${Math.abs(daysOffset)} days after the start date.`;
      }
    } else if (coursesPreApprovalSubmissionMinLeadTime) {
      daysOffset = Number(coursesPreApprovalSubmissionMinLeadTime) || null;
      if (daysOffset) {
        policyMessage = `Your employer requires that you submit your application at a minimum of ${daysOffset} days before the start date.`;
      }
    }

    if (!daysOffset) {
      return null;
    }

    const anchor = submittedAt ? new Date(submittedAt) : new Date();
    const deadlineDate = this.addDays(isNaN(anchor.getTime()) ? new Date() : anchor, daysOffset);

    return {
      date: deadlineDate,
      message: policyMessage,
    };
  }

  /**
   * Lower bound for the begin-date validation schema, falling back to a sentinel far in
   * the past when no lead-time policy applies.
   *
   * @param programTemplate template carrying the lead-time policy fields
   * @param submittedAt when the application was first submitted, as an ISO timestamp;
   *   omit for a never-submitted draft, which is then measured from today
   * @return the effective lower bound and the message to show when a date falls below it
   */
  courseMinimumBeginDateWithErrorMessaging(
    programTemplate: TASProgramTemplateModel,
    submittedAt?: string
  ): CourseDateValidationResult {
    const lastDayToSubmit = this.lastPossibleBeginDateWithErrorMessaging(
      programTemplate,
      submittedAt
    );
    if (!lastDayToSubmit) {
      return {
        date: new Date('2000-01-01'),
        message: this.intl.t(
          'tuition_assistance.program_details.courses.validation.defaults.begin_date'
        ),
      };
    }

    return lastDayToSubmit;
  }

  /**
   * Valibot schema validating a `{ dateEntered }` model against the template's begin-date
   * policy: the pre-approval lead-time lower bound, the max-lead-time upper bound, and
   * the course end date where one is supplied.
   *
   * Only the lower bound honours `submittedAt`; the upper bound is today plus the max lead
   * time, so it only ever loosens as time passes and can never newly reject a date a first
   * submission accepted.
   *
   * @param programTemplate template carrying the begin-date policy fields
   * @param endDate course end date, used as the upper bound when earlier than the
   *   max-lead-time bound; ignored when absent or unparseable
   * @param submittedAt when the application was first submitted, as an ISO timestamp; the
   *   lower bound is measured from it rather than from today. Omit for a never-submitted draft
   * @return schema whose parse issues carry the policy messages to show the employee
   */
  dynamicBeginDateValidationSchema = (
    programTemplate: TASProgramTemplateModel,
    endDate?: string,
    submittedAt?: string
  ) => {
    const maxDate = this.courseMaximumBeginDateWithErrorMessaging(programTemplate);
    const minDate = this.courseMinimumBeginDateWithErrorMessaging(programTemplate, submittedAt);

    // If end date is provided and valid, start date must be on or before it
    const endDateConstraint =
      endDate && !isNaN(new Date(endDate).getTime())
        ? {
            date: new Date(endDate),
            message: this.intl.t(
              'tuition_assistance.program_details.courses.validation.start_before_end'
            ),
          }
        : null;

    // Use the earlier of maxDate or endDate as the upper bound
    const effectiveMaxDate =
      endDateConstraint && (!maxDate || endDateConstraint.date < maxDate.date)
        ? endDateConstraint
        : maxDate;

    const validators = [
      date(),
      ...(minDate ? [minValue<Date, Date, string>(minDate.date, minDate.message)] : []),
      ...(effectiveMaxDate
        ? [maxValue<Date, Date, string>(effectiveMaxDate.date, effectiveMaxDate.message)]
        : []),
    ] as const;

    const validation = object({
      dateEntered: pipe(...validators),
    });

    return validation;
  };
}
